import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, AlertCircle, ArrowLeft } from "lucide-react";
import { Layout } from "../components/Layout";
import { useAdmin } from "../lib/admin";
import { useDocumentMeta } from "../lib/useDocumentMeta";

export const AdminLoginPage = () => {
  const { login, admin, loading } = useAdmin();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useDocumentMeta({
    title: "Admin · Will Not Be Forgotten",
    description: "Administrative access for moderating community submissions to the Will Not Be Forgotten archive.",
  });

  useEffect(() => {
    if (admin) navigate("/admin/dashboard", { replace: true });
  }, [admin, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not sign in.";
      setError(typeof msg === "string" ? msg : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout headerVariant="solid" hideFooter>
      <section data-testid="admin-login" className="section" style={{ paddingTop: "5rem", borderTop: "none" }}>
        <div className="shell" style={{ maxWidth: 540 }}>
          <Link to="/" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            color: "hsl(var(--muted))", fontSize: "0.85rem",
            letterSpacing: "0.06em", textTransform: "uppercase",
            textDecoration: "none", marginBottom: "1.5rem",
          }}>
            <ArrowLeft size={14} aria-hidden /> Back to archive
          </Link>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            color: "hsl(var(--accent))", marginBottom: 12,
          }}>
            <ShieldCheck size={16} aria-hidden />
            <span style={{
              fontSize: "0.78rem", letterSpacing: "0.18em",
              textTransform: "uppercase", fontWeight: 600,
            }}>
              Restricted access
            </span>
          </div>
          <h1 style={{ fontSize: "clamp(2rem, 3vw, 2.6rem)", marginTop: 0, marginBottom: "0.5rem" }}>Sign in to moderate.</h1>
          <p style={{ marginTop: 0, marginBottom: "2rem" }}>
            Sign in to review community submissions, approve or reject pending entries, and curate the featured list.
          </p>

          <form onSubmit={onSubmit} data-testid="admin-login-form" noValidate style={{
            border: "1px solid hsl(var(--line))",
            background: "hsl(var(--bg-elev))",
            padding: "1.75rem",
            display: "flex", flexDirection: "column", gap: "1rem",
          }}>
            <Field label="Email">
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                style={inputStyle}
                autoComplete="username"
              />
            </Field>
            <Field label="Password">
              <input
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                style={inputStyle}
                autoComplete="current-password"
              />
            </Field>

            {error && (
              <div role="alert" data-testid="admin-login-error" style={errorBox}>
                <AlertCircle size={15} aria-hidden /> {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || loading}
              data-testid="admin-login-button"
              style={{ alignSelf: "flex-end" }}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
};

const Field = ({ label, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <span style={{
      fontSize: "0.78rem",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "hsl(var(--muted))",
      fontWeight: 500,
    }}>{label}</span>
    {children}
  </label>
);

const inputStyle = {
  width: "100%",
  padding: "0.7rem 0.85rem",
  border: "1px solid hsl(var(--line))",
  background: "hsl(var(--bg))",
  color: "hsl(var(--ink))",
  fontSize: "0.95rem",
  fontFamily: "var(--font-body)",
  borderRadius: 2,
};

const errorBox = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "0.7rem 0.9rem",
  fontSize: "0.9rem",
  border: "1px solid hsl(var(--destructive) / 0.5)",
  background: "hsl(var(--destructive) / 0.08)",
  color: "hsl(var(--destructive))",
};
